import styles from './ScheduledEmailsTab.module.scss';
import { FC, ReactNode, useState } from 'react';
import { NotificationManager } from 'react-notifications';
import { ServiceScheduledEmails, RealtimeEmailVariant } from '../../services/service-scheduled-emails';
import { TabPage } from '../../components/shared/PageWithTabs/TabPage';
import { TabsType } from '../../components/shared/PageWithTabs/PageWithTabs';
import Card from '../../components/shared/Card/card';
import Field from '../../components/shared/Field/Field';
import BtButton from '../bt-button/bt-button';

type Props = { tabs: TabsType };

type SendResult = { success: boolean; message: string };

const useTestSend = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const send = async (request: (email: string) => Promise<SendResult>, fallbackSuccess: string) => {
    if (!email) return;
    setLoading(true);
    try {
      const result = await request(email);
      if (result.success) {
        NotificationManager.success(result.message || fallbackSuccess);
      } else {
        NotificationManager.error(result.message || 'Failed to send test email');
      }
    } catch (err: any) {
      NotificationManager.error(err?.response?.data?.message || err?.message || 'Failed to send test email');
    } finally {
      setLoading(false);
    }
  };

  return { email, setEmail, loading, send };
};

// The recipient field + "Send Test" row every debug card ends with. Extra
// controls (variant select, via-queue checkbox) render before/after the field
// and receive the loading flag so they can disable themselves during a send.
type TestSendRowProps = {
  onSend: (email: string) => Promise<SendResult>;
  successMessage: string;
  before?: (loading: boolean) => ReactNode;
  after?: (loading: boolean) => ReactNode;
};

const TestSendRow: FC<TestSendRowProps> = ({ onSend, successMessage, before, after }) => {
  const { email, setEmail, loading, send } = useTestSend();
  return (
    <div className={styles.row}>
      {before?.(loading)}
      <div className={styles.recipient}>
        <Field
          type="email"
          placeholder="recipient@example.com"
          value={email}
          onChange={setEmail}
          disabled={loading}
        />
      </div>
      {after?.(loading)}
      <BtButton onClick={() => send(onSend, successMessage)} disabled={loading || !email}>
        Send Test
      </BtButton>
    </div>
  );
};

const REALTIME_VARIANTS: { value: RealtimeEmailVariant; label: string }[] = [
  { value: 'bidReceived', label: 'New bid received' },
  { value: 'intentReceivedAccepted', label: 'Intent: will bid' },
  { value: 'intentReceivedDeclined', label: 'Intent: declines, with reason' },
];

export const ScheduledEmailsTab: FC<Props> = ({ tabs }) => {
  const [variant, setVariant] = useState<RealtimeEmailVariant>('bidReceived');
  const [viaQueue, setViaQueue] = useState(false);

  return (
    <TabPage tabs={tabs} title="Email Debug">
      <p className={styles.intro}>
        Debug tools for emails sent on a schedule by Azure Functions.
      </p>

      <Card title="Supplier Liability Insurance Reminder">
        <p className={styles.description}>
          Sends a sample reminder with dummy data — recipient "Erik Andersson",
          insurance type "Ansvarsförsäkring", expired 12 days ago.
        </p>
        <TestSendRow
          onSend={(email) => ServiceScheduledEmails.sendTestSupplierInsuranceReminder(email)}
          successMessage="Test email sent"
        />
      </Card>

      <Card title="Bid Request Reminder">
        <p className={styles.description}>
          Sends the reminder a supplier gets on the morning of the day the purchaser scheduled —
          the bid request email again, subject prefixed &quot;Påminnelse:&quot; — with dummy data
          (recipient &quot;Anna&quot;, project &quot;Kvarteret Eken&quot;, version 2).
        </p>
        <TestSendRow
          onSend={(email) => ServiceScheduledEmails.sendTestBidRequestReminder(email)}
          successMessage="Test email sent"
        />
      </Card>

      <Card title="Real-time Bid / Intent Email">
        <p className={styles.description}>
          Sends a sample "new bid" or "intent to bid" notification with dummy data
          (supplier "Elfirman &amp; Söner AB", project "Kvarteret Eken").
          With <strong>via queue</strong> checked, the message travels the real pipeline —
          storage queue → function app → send endpoint → SMTP — so it also verifies the
          queue config and the consumer; unchecked, the backend renders and sends directly.
        </p>
        <TestSendRow
          onSend={(email) => ServiceScheduledEmails.sendTestRealtimeEmail(email, variant, viaQueue)}
          successMessage={viaQueue ? 'Queued — arriving shortly' : 'Test email sent'}
          before={(loading) => (
            <select
              value={variant}
              onChange={(e) => setVariant(e.target.value as RealtimeEmailVariant)}
              disabled={loading}
              className={styles.select}
            >
              {REALTIME_VARIANTS.map(v => (
                <option key={v.value} value={v.value}>{v.label}</option>
              ))}
            </select>
          )}
          after={(loading) => (
            <label className={styles.viaQueue}>
              <input
                type="checkbox"
                checked={viaQueue}
                onChange={(e) => setViaQueue(e.target.checked)}
                disabled={loading}
              />
              via queue
            </label>
          )}
        />
      </Card>

      <Card title="Daily Message Digest">
        <p className={styles.description}>
          Sends the morning digest with dummy data — two organizations, mixed unread
          counts — exactly as a multi-org buyer would receive it. Rendered and sent
          directly (the real digest's content depends on a user's actual unread messages).
        </p>
        <TestSendRow
          onSend={(email) => ServiceScheduledEmails.sendTestMessageDigest(email)}
          successMessage="Test email sent"
        />
      </Card>
    </TabPage>
  );
};
